import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GroupsIcon from '@mui/icons-material/Groups';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MainLayout from '../../layout/MainLayout';
import CohortBuilder from './cohorts/CohortBuilder';

// Import data
import parametersData from '../../data/parameters.json';
import parameterListsData from '../../data/parameterLists.json';
import cohortsData from '../../data/cohorts.json';

const Cohorts = () => {
  const [view, setView] = useState('list'); // 'list', 'create', 'edit'
  const [cohorts, setCohorts] = useState(cohortsData.cohorts);
  const [editingCohort, setEditingCohort] = useState(null);
  const [selectedListId, setSelectedListId] = useState('');
  const [loadedParameters, setLoadedParameters] = useState([]);
  const [success, setSuccess] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Create new cohort
  const handleCreateNew = () => {
    setEditingCohort(null);
    setSelectedListId('');
    setLoadedParameters([]);
    setView('create');
  };

  // Edit existing cohort
  const handleEdit = (cohort) => {
    setEditingCohort(cohort);
    setSelectedListId(cohort.parameterListId);

    // Load parameters from the cohort's list
    const selectedList = parameterListsData.lists.find(list => list.id === cohort.parameterListId);
    if (selectedList) {
      const parameters = parametersData.parameters.filter(param =>
        typeof param.id === 'number' && selectedList.parameterIds.includes(param.id)
      );
      setLoadedParameters(parameters);
    }

    setView('edit');
  };

  // Delete cohort
  const handleDelete = (cohortId) => {
    if (window.confirm('Möchten Sie diese Kohorte wirklich löschen?')) {
      setCohorts(cohorts.filter(c => c.id !== cohortId));
      setSuccess('Kohorte erfolgreich gelöscht');
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  // Duplicate cohort
  const handleDuplicate = (cohort) => {
    const newCohort = {
      ...cohort,
      id: `cohort-${Date.now()}`,
      name: `${cohort.name} (Kopie)`,
      createdAt: new Date().toISOString().split('T')[0],
      createdBy: 'Aktueller Benutzer',
      lastModified: new Date().toISOString().split('T')[0],
      isPublic: cohort.isPublic,
      usageCount: 0
    };
    setCohorts([...cohorts, newCohort]);
    setSuccess('Kohorte erfolgreich dupliziert');
    setTimeout(() => setSuccess(''), 3000);
  };

  // Load parameters from selected list
  const handleLoadList = (listId) => {
    const selectedList = parameterListsData.lists.find(list => list.id === listId);
    if (!selectedList) return;

    const parameters = parametersData.parameters.filter(param =>
      typeof param.id === 'number' && selectedList.parameterIds.includes(param.id)
    );

    setLoadedParameters(parameters);
    setSelectedListId(listId);
  };

  // Save cohort (create or update)
  const handleSaveCohort = (cohortData) => {
    const selectedList = parameterListsData.lists.find(list => list.id === selectedListId);

    if (editingCohort) {
      // Update existing cohort
      const updatedCohort = {
        ...editingCohort,
        ...cohortData,
        lastModified: new Date().toISOString().split('T')[0],
        parameterListId: selectedListId,
        parameterListName: selectedList?.name || '',
        isPublic: cohortData.isPublic !== undefined ? cohortData.isPublic : editingCohort.isPublic,
      };
      setCohorts(cohorts.map(c => c.id === editingCohort.id ? updatedCohort : c));
      setSuccess('Kohorte erfolgreich aktualisiert!');
    } else {
      // Create new cohort
      const newCohort = {
        id: `cohort-${Date.now()}`,
        ...cohortData,
        createdAt: new Date().toISOString().split('T')[0],
        createdBy: 'Aktueller Benutzer',
        lastModified: new Date().toISOString().split('T')[0],
        parameterListId: selectedListId,
        parameterListName: selectedList?.name || '',
        isPublic: cohortData.isPublic !== undefined ? cohortData.isPublic : true,
        patientCount: 0, // Would be calculated from actual query
        usageCount: 0
      };
      setCohorts([...cohorts, newCohort]);
      setSuccess('Kohorte erfolgreich erstellt!');
    }

    setTimeout(() => setSuccess(''), 3000);
    setView('list');
  };

  // Cancel and go back to list
  const handleCancel = () => {
    setView('list');
    setEditingCohort(null);
    setSelectedListId('');
    setLoadedParameters([]);
  };

  // Export cohort as JSON
  const handleExport = (cohort) => {
    const dataStr = JSON.stringify(cohort, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `kohorte_${cohort.name.replace(/\s+/g, '_').toLowerCase()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setSuccess(`Kohorte "${cohort.name}" erfolgreich exportiert`);
    setTimeout(() => setSuccess(''), 3000);
  };

  // Open import dialog
  const handleOpenImport = () => {
    setImportDialogOpen(true);
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedCohort = JSON.parse(e.target.result);

        // Validate required fields
        if (!importedCohort.name || !importedCohort.includeConditions) {
          alert('Ungültiges Kohortenformat: Name und Einschlusskriterien fehlen');
          return;
        }

        // Create new cohort from imported data
        const newCohort = {
          id: `cohort-${Date.now()}`,
          name: `${importedCohort.name} (Importiert)`,
          description: importedCohort.description || '',
          includeConditions: importedCohort.includeConditions || [],
          excludeConditions: importedCohort.excludeConditions || [],
          parameterListId: importedCohort.parameterListId || '',
          parameterListName: importedCohort.parameterListName || '',
          isPublic: importedCohort.isPublic !== undefined ? importedCohort.isPublic : false,
          createdAt: new Date().toISOString().split('T')[0],
          createdBy: 'Aktueller Benutzer',
          lastModified: new Date().toISOString().split('T')[0],
          patientCount: 0,
          usageCount: 0
        };

        setCohorts([...cohorts, newCohort]);
        setImportDialogOpen(false);
        setSuccess(`Kohorte "${newCohort.name}" erfolgreich importiert`);
        setTimeout(() => setSuccess(''), 3000);

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        alert('Fehler beim Lesen der JSON-Datei: ' + error.message);
      }
    };
    reader.readAsText(file);
  };

  // Render cohort list view
  const renderListView = () => (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Kohorten
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Verwalten Sie Ihre Patientenkohorten
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={handleOpenImport}
            size="large"
          >
            Importieren
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateNew}
            size="large"
          >
            Neue Kohorte erstellen
          </Button>
        </Box>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Parameterliste</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Kriterien</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Patienten</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Sichtbarkeit</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Erstellt</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="center">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cohorts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    Noch keine Kohorten definiert. Klicken Sie auf "Neue Kohorte erstellen" um zu beginnen.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              cohorts.map((cohort) => (
                <TableRow key={cohort.id} hover>
                  <TableCell>
                    <Typography variant="body1" fontWeight="bold">
                      {cohort.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      von {cohort.createdBy}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {cohort.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={cohort.parameterListName}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Chip
                        label={`${cohort.includeConditions.length} Ein`}
                        size="small"
                        color="success"
                        sx={{ fontSize: '0.7rem' }}
                      />
                      {cohort.excludeConditions.length > 0 && (
                        <Chip
                          label={`${cohort.excludeConditions.length} Aus`}
                          size="small"
                          color="error"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Geschätzte Patientenanzahl">
                      <Chip
                        icon={<GroupsIcon />}
                        label={cohort.patientCount}
                        size="small"
                        color="info"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={cohort.isPublic ? 'Öffentlich - für alle Benutzer sichtbar' : 'Privat - nur für Sie sichtbar'}>
                      <Chip
                        icon={cohort.isPublic ? <PublicIcon /> : <LockIcon />}
                        label={cohort.isPublic ? 'Öffentlich' : 'Privat'}
                        size="small"
                        color={cohort.isPublic ? 'success' : 'default'}
                        variant={cohort.isPublic ? 'filled' : 'outlined'}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {cohort.createdAt}
                    </Typography>
                    {cohort.lastModified !== cohort.createdAt && (
                      <Typography variant="caption" color="text.secondary">
                        geändert: {cohort.lastModified}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="Bearbeiten">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(cohort)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Als JSON exportieren">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleExport(cohort)}
                        >
                          <FileDownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplizieren">
                        <IconButton
                          size="small"
                          onClick={() => handleDuplicate(cohort)}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(cohort.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // Render builder view
  const renderBuilderView = () => (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mb: 2 }}
        >
          Zurück zur Übersicht
        </Button>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          {editingCohort ? 'Kohorte bearbeiten' : 'Neue Kohorte erstellen'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {editingCohort
            ? `Bearbeiten Sie die Kohorte "${editingCohort.name}"`
            : 'Wählen Sie eine Parameterliste und definieren Sie Ihre Kohortenkriterien'
          }
        </Typography>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <CohortBuilder
        selectedParameters={loadedParameters}
        selectedListId={selectedListId}
        parameterLists={parameterListsData.lists}
        onLoadList={handleLoadList}
        onSave={handleSaveCohort}
        onCancel={handleCancel}
        initialData={editingCohort}
      />
    </Box>
  );

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        {view === 'list' ? renderListView() : renderBuilderView()}
      </Box>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)}>
        <DialogTitle>Kohorte importieren</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Wählen Sie eine JSON-Datei aus, um eine Kohorte zu importieren. Die Datei sollte eine zuvor exportierte Kohorte enthalten.
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="cohort-file-input"
            />
            <label htmlFor="cohort-file-input">
              <Button
                variant="contained"
                component="span"
                startIcon={<UploadFileIcon />}
                fullWidth
              >
                JSON-Datei auswählen
              </Button>
            </label>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>
            Abbrechen
          </Button>
        </DialogActions>
      </Dialog>
    </MainLayout>
  );
};

export default Cohorts;
